import { ActionIcon, Group, ScrollArea, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAgentSession } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { baseBlack, baseWhite, inkLight, inkLighter, secondaryBase, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { useShowAgentMinerApiCost } from '../shared/agent-miner.hooks';
import { AgentSourceIcon } from '../shared/agent-source-icon';
import { turnDisplayNumber } from '../shared/formatters';
import { EffortBadge, ModelBadge } from '../shared/model-effort-badges';
import { getSessionDisplayTitle } from '../shared/shared.helpers';
import { DIVIDER_MARGIN } from './turn-panel.constants';
import { buildTurnMetadata } from './turn-panel.helpers';
import { Body, MetaLabel, MetaValue, Prompt, SectionLabel, Wrapper } from './turn-panel.styles';
import type { SessionDetailLocationState } from '../session-detail/session-detail.type';
import type { TurnPanelProps } from './turn-panel.type';

/**
 * Side panel summarizing a single agent turn: its model and effort, prompt,
 * timing, tokens and cost. The full transcript lives on the session details
 * page, which "Expand details" opens scrolled to this turn.
 * Reads the parent session through `useAgentSession`, so it shares the session
 * panel's cache entry and opening a turn from an open session costs no request.
 *
 * @param {TurnPanelProps} props - Session and turn ids plus navigation callbacks
 * @returns {JSX.Element} The rendered turn panel
 */
export function TurnPanel({ sessionId, turnId, onBack, onClose, returnTo }: TurnPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session, query } = useAgentSession(sessionId);
  const showApiCost = useShowAgentMinerApiCost();

  const turns = session?.turns ?? [];
  const turn = turns.find((candidate) => candidate.id === turnId);

  const headerActions = (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Group gap={8} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
        <ActionIcon
          variant="subtle"
          size="sm"
          radius="xl"
          color={skyDark}
          onClick={onBack}
          aria-label={t('common.actions.back')}
        >
          <Icon name="arrow_back" size={18} color={skyDark} />
        </ActionIcon>
        {turn && (
          <Text weight="bold" size="medium" color={secondaryBase}>
            Turn {turnDisplayNumber(turn.turn_number)} of {turns.length}
          </Text>
        )}
      </Group>
      <ActionIcon
        variant="subtle"
        size="sm"
        radius="xl"
        color={skyDark}
        onClick={onClose}
        aria-label={t('common.actions.close')}
      >
        <Icon name="close" size={18} color={skyDark} />
      </ActionIcon>
    </Group>
  );

  if (!session || !turn) {
    // Loading keeps the back/close affordances visible; once loaded, a missing
    // turn (stale link, or the turn was removed) gets an explicit message.
    const isMissing = !query.isLoading;
    return (
      <Wrapper>
        <BrandedLoadingOverlay visible={query.isLoading} variant="colored" />
        <Body>
          {headerActions}
          {isMissing && (
            <Text size="small" color={inkLight} style={{ marginTop: 12 }}>
              {query.isError ? 'Unable to load this turn.' : 'This turn is no longer available.'}
            </Text>
          )}
        </Body>
      </Wrapper>
    );
  }

  const metadata = buildTurnMetadata(turn, showApiCost);

  // Opens the transcript on the session details page, focused on this turn.
  const goToTurnDetails = () => {
    const state: SessionDetailLocationState | undefined = returnTo ? { returnTo } : undefined;
    const search = new URLSearchParams({ turn: turn.id }).toString();
    navigate(`/application/agent-miner/sessions/${session.id}?${search}`, { state });
  };

  return (
    <Wrapper>
      {/* Scrollbar hidden; the panel still scrolls by wheel, trackpad and keyboard. */}
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="never">
        <Body>
          {headerActions}

          <Group gap={6} mt={6} align="center" wrap="nowrap">
            <AgentSourceIcon source={session.source} size={16} />
            <Text size="small" color={inkLight}>
              {getSessionDisplayTitle(session)}
            </Text>
          </Group>

          {(turn.model || turn.effort) && (
            <Group gap={6} mt={8} align="center">
              {turn.model && <ModelBadge title={turn.model}>{turn.model}</ModelBadge>}
              {turn.effort && <EffortBadge title={`Reasoning effort: ${turn.effort}`}>{turn.effort}</EffortBadge>}
            </Group>
          )}

          {turn.user_prompt && (
            <>
              <Divider style={DIVIDER_MARGIN} />
              <SectionLabel>Prompt</SectionLabel>
              <Prompt>{turn.user_prompt}</Prompt>
            </>
          )}

          <Divider style={DIVIDER_MARGIN} />

          <Stack gap={6}>
            {metadata.map((item) => (
              <Group key={item.label} justify="space-between" wrap="nowrap" gap={8}>
                <MetaLabel>
                  <Icon name={item.iconName} size={14} color={inkLighter} />
                  <Text size="small" color={inkLighter}>
                    {item.label}
                  </Text>
                </MetaLabel>
                <MetaValue title={item.value}>
                  <Text size="small" color={baseBlack}>
                    {item.value}
                  </Text>
                </MetaValue>
              </Group>
            ))}
          </Stack>

          <Divider style={DIVIDER_MARGIN} />

          <Button
            fullWidth
            size="sm"
            radius="xl"
            variant="secondary"
            onClick={goToTurnDetails}
            leftSection={<Icon name="open_in_full" variant="filled" size={14} color={baseWhite} />}
          >
            Expand details
          </Button>
        </Body>
      </ScrollArea>
    </Wrapper>
  );
}
